// Reusable master CRUD list (Jul-2026) — built for the Invoice Queue
// "Manage Masters" dialog; intentionally generic so it can later move into
// Company Setup / Administration without backend changes.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";

const inputCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";

export function MasterCrudList({ title, load, create, update, remove, testid }) {
  const [rows, setRows] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState(null); // {id, label}
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try { setRows(await load()); } catch { /* noop */ }
  }, [load]);
  useEffect(() => { refresh(); }, [refresh]);

  async function run(fn, ok) {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">{title}</div>
      <div className="flex gap-2 mb-2">
        <input data-testid={`${testid}-new-label`} value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
          placeholder="New entry…" className={inputCls} />
        <button data-testid={`${testid}-add`} disabled={busy || !newLabel.trim()}
          onClick={() => run(() => create(newLabel.trim()).then(() => setNewLabel("")), "Added")}
          className="bg-slate-900 text-white px-3 py-2 text-xs font-bold flex items-center gap-1 disabled:opacity-40">
          <Plus size={12} /> Add
        </button>
      </div>
      <div className="border border-slate-200 max-h-64 overflow-auto">
        {rows.length === 0 && <div className="px-3 py-4 text-xs text-slate-400 text-center">No entries.</div>}
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-2 px-2.5 py-1.5 border-b border-slate-50 text-xs" data-testid={`${testid}-row-${r.id}`}>
            {editing?.id === r.id ? (
              <>
                <input autoFocus value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} className={inputCls} />
                <button title="Save" disabled={busy} onClick={() => run(() => update(r.id, { label: editing.label.trim() }).then(() => setEditing(null)), "Renamed")}
                  className="p-1 border border-emerald-300 text-emerald-700"><Check size={11} /></button>
                <button title="Cancel" onClick={() => setEditing(null)} className="p-1 border border-slate-300 text-slate-500"><X size={11} /></button>
              </>
            ) : (
              <>
                <span className={`flex-1 truncate ${r.active === false ? "line-through text-slate-400" : ""}`}>{r.label}</span>
                <label className="flex items-center gap-1 text-[10px] text-slate-500">
                  <input type="checkbox" checked={r.active !== false} disabled={busy}
                    onChange={() => run(() => update(r.id, { active: r.active === false }), r.active === false ? "Activated" : "Deactivated")}
                    className="accent-[#F97316]" /> active
                </label>
                <button title="Rename" onClick={() => setEditing({ id: r.id, label: r.label })}
                  className="p-1 border border-slate-300 text-slate-500 hover:bg-slate-50"><Pencil size={11} /></button>
                <button title="Delete" disabled={busy}
                  onClick={() => window.confirm(`Delete "${r.label}"?`) && run(() => remove(r.id), "Deleted")}
                  className="p-1 border border-rose-300 text-rose-600 hover:bg-rose-50"><Trash2 size={11} /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
